import {
  FieldValues,
  Path,
  UseFormSetValue,
  UseFormWatch,
} from "react-hook-form";
import { UseQueryResult } from "react-query";

import { getVehicleMakes } from "../api/vehicle/getVehicleMakes";
import { getVehicleModels } from "../api/vehicle/getVehicleModels";
import { getVehicleModelYears } from "../api/vehicle/getVehicleModelYears";
import { getVehicleVariants } from "../api/vehicle/getVehicleVariants";
import { AutoCompleteOption } from "../components/form/APAutoComplete";
import { buildAutoCompleteOptions } from "../helpers/buildOptions";
import { APQueryOptions } from "../providers/APQueryProvider";
import {
  VehicleMake,
  VehicleModel,
  VehicleModelYear,
  VehicleVariant,
} from "../types/Vehicles";
import { useCascadeReset } from "./useCascadeReset";

export type VehicleCascadeFields = {
  vehicleMake: AutoCompleteOption | null;
  vehicleModel: AutoCompleteOption | null;
  vehicleYear: AutoCompleteOption | null;
  vehicleVariant: AutoCompleteOption | null;
};

type UseVehicleCascadeParams<
  TFieldValues extends FieldValues & VehicleCascadeFields
> = {
  watch: UseFormWatch<TFieldValues>;
  setValue: UseFormSetValue<TFieldValues>;
  disabled?: boolean;
  makesQueryOptions?: APQueryOptions;
};

export type UseVehicleCascadeReturn = {
  watchVehicleMake: AutoCompleteOption | null;
  watchVehicleModel: AutoCompleteOption | null;
  watchVehicleYear: AutoCompleteOption | null;
  watchVehicleVariant: AutoCompleteOption | null;
  makeOptions: AutoCompleteOption[];
  modelOptions: AutoCompleteOption[];
  yearOptions: AutoCompleteOption[];
  variantOptions: AutoCompleteOption[];
  makes: UseQueryResult<VehicleMake[]>;
  models: UseQueryResult<VehicleModel[]>;
  modelYears: UseQueryResult<VehicleModelYear[]>;
  variants: UseQueryResult<VehicleVariant[]>;
  isMakeFieldDisabled: boolean;
  isModelFieldDisabled: boolean;
  isYearFieldDisabled: boolean;
  isVariantFieldDisabled: boolean;
};

export const useVehicleCascade = <
  TFieldValues extends FieldValues & VehicleCascadeFields
>({
  watch,
  setValue,
  disabled = false,
  makesQueryOptions = {},
}: UseVehicleCascadeParams<TFieldValues>): UseVehicleCascadeReturn => {
  const watchVehicleMake = useCascadeReset({
    watch,
    setValue,
    parentField: "vehicleMake" as Path<TFieldValues>,
    childField: "vehicleModel" as Path<TFieldValues>,
  });
  const watchVehicleModel = useCascadeReset({
    watch,
    setValue,
    parentField: "vehicleModel" as Path<TFieldValues>,
    childField: "vehicleYear" as Path<TFieldValues>,
  });
  const watchVehicleYear = useCascadeReset({
    watch,
    setValue,
    parentField: "vehicleYear" as Path<TFieldValues>,
    childField: "vehicleVariant" as Path<TFieldValues>,
  });
  const watchVehicleVariant = watch(
    "vehicleVariant" as Path<TFieldValues>
  ) as AutoCompleteOption | null;

  const makes = getVehicleMakes(makesQueryOptions);
  const models = getVehicleModels(watchVehicleMake?.id || null, {
    disabled: !watchVehicleMake,
  });
  const modelYears = getVehicleModelYears(watchVehicleModel?.id || null, {
    disabled: !watchVehicleModel,
  });
  const variants = getVehicleVariants(
    watchVehicleMake?.id || null,
    watchVehicleModel?.id || null,
    watchVehicleYear?.id || null,
    {
      disabled: !watchVehicleMake || !watchVehicleModel || !watchVehicleYear,
    }
  );

  const makeOptions = makes.isSuccess
    ? buildAutoCompleteOptions(makes.data, "name", "id")
    : [];
  const modelOptions = models.isSuccess
    ? buildAutoCompleteOptions(models.data, "name", "id")
    : [];
  const yearOptions = modelYears.isSuccess
    ? buildAutoCompleteOptions(
        modelYears.data.map((year) => ({ id: `${year}`, name: `${year}` })),
        "name",
        "id"
      )
    : [];
  const variantOptions = variants.isSuccess
    ? buildAutoCompleteOptions(variants.data, "name", "id")
    : [];

  return {
    watchVehicleMake,
    watchVehicleModel,
    watchVehicleYear,
    watchVehicleVariant,
    makeOptions,
    modelOptions,
    yearOptions,
    variantOptions,
    makes,
    models,
    modelYears,
    variants,
    isMakeFieldDisabled: disabled,
    isModelFieldDisabled: disabled || !watchVehicleMake || !models.isSuccess,
    isYearFieldDisabled:
      disabled ||
      !watchVehicleMake ||
      !watchVehicleModel ||
      !modelYears.isSuccess,
    isVariantFieldDisabled:
      disabled ||
      !watchVehicleMake ||
      !watchVehicleModel ||
      !watchVehicleYear ||
      !variants.isSuccess,
  };
};
